import React, { useEffect, useState } from "react";

import { convertFullDateTimeToWithoutYearSecond } from "../../../libs/dateFormat";

import { useUserContext } from "../../../contexts/userContext";

import {
  ServiceType,
  postEtcSatisfactionReply,
} from "../../../services/etcServices";
import { postIncommoditySatisfactionReply } from "../../../services/mangerReportServices";
import { SatisfactionRequest } from "../../../services/userReportServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";

import StarScore from "../../Talk/StarScore";
import CommonTextArea from "../../TextArea";
import CommonButton from "../../Button";
import CommonProfile from "../../MyInfo/Profile";
import UserSatisfactionPopup from "../../Popup/UserSatisfaction";

export type SatisfactionItem = {
  id: number;
  score: number;
  comment: string | null;
  createdAt: string | null;
  createdBy: number;
  createdByName: string | null;
  createdByThumbnail: string | null;
  reply?: SatisfactionReplyItem;
};
export type SatisfactionReplyItem = Omit<SatisfactionItem, "score">;

type Props = {
  satisfaction: SatisfactionItem;
  isManager?: boolean;
  type: ServiceType;
  serviceId: string;
  updateDisabled?: boolean;
  onLoad?: () => void;
  open?: (message: string) => void;
  postSatisfaction: (
    satisfactionId: number,
    satisfaction: SatisfactionRequest
  ) => Promise<void>;
  setIsLoading?: (isLoading: boolean) => void;
};

export default function HistorySatisfaction({
  satisfaction,
  isManager,
  serviceId,
  type,
  updateDisabled = false,
  onLoad,
  open,
  postSatisfaction,
  setIsLoading,
}: Props) {
  const [user] = useUserContext();
  const [popup, setPopup] = useState<string>("");

  return (
    <>
      <div className="flex flex-col gap-5 w-full">
        <div className="flex gap-2.5 w-full">
          <div>
            <CommonProfile src={satisfaction?.createdByThumbnail} />
          </div>

          <div className="flex flex-col gap-3 w-full">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-[5px]">
                <div>
                  <StarScore score={satisfaction.score ?? 0} />
                </div>
                <div className=" text-sm text-brand-neutral-85 font-semibold">
                  {satisfaction.createdByName ?? ""}
                </div>
              </div>
              <div className="text-xs text-[#999999]">
                {convertFullDateTimeToWithoutYearSecond(
                  satisfaction.createdAt as string
                )}
              </div>
            </div>
            <div className="text-base break-all">{satisfaction.comment}</div>
            {!isManager &&
              satisfaction.createdBy === user.memberId &&
              !updateDisabled && (
                <CommonButton
                  variant="tertiary"
                  className="w-full"
                  onClick={() => {
                    setPopup("satisfaction");
                  }}
                >
                  만족도 평가 수정
                </CommonButton>
              )}
          </div>
        </div>

        {(satisfaction.reply || isManager) && (
          <HistorySatisfactionReply
            satisfaction={satisfaction.reply}
            serviceId={serviceId}
            type={type}
            onLoad={onLoad}
            isManager={isManager}
          />
        )}
      </div>

      <UserSatisfactionPopup
        popup={popup}
        setPopup={setPopup}
        open={open}
        postSatisfaction={(satisfactionRequest: SatisfactionRequest) => {
          return postSatisfaction(satisfaction.id, satisfactionRequest);
        }}
        score={satisfaction.score}
        content={satisfaction?.comment ?? ""}
        setIsLoading={setIsLoading}
      />
    </>
  );
}

export type HistorySatisfactionReplyProps = {
  satisfaction?: SatisfactionReplyItem;
  serviceId: string;
  type: ServiceType;
  onLoad?: () => void;
  isManager?: boolean;
};

export const HistorySatisfactionReply = ({
  satisfaction,
  serviceId,
  type,
  onLoad,
  isManager,
}: HistorySatisfactionReplyProps) => {
  const [user] = useUserContext();
  const [comment, setComment] = useState<string>(satisfaction?.comment ?? "");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open }] = useSimplePopup({});

  useEffect(() => {
    setComment(satisfaction?.comment ?? "");
  }, [satisfaction?.comment]);

  return (
    <>
      {loadingComponent}
      {satisfaction ? (
        <div className="pl-[50px]">
          <div className="p-[15px] rounded-[5px] flex flex-col gap-2.5 bg-[#f5f5f5]">
            <div className="flex gap-[5px] items-center">
              <CommonProfile
                src={satisfaction?.createdByThumbnail ?? undefined}
                sizeVariant="reply"
              />
              <div className="flex-1 text-sm font-medium">
                {satisfaction?.createdByName ?? ""}
              </div>
              <div className="text-xs text-[#999999]">
                {convertFullDateTimeToWithoutYearSecond(
                  satisfaction?.createdAt as string
                )}
              </div>
            </div>
            <div>{comment}</div>
          </div>
        </div>
      ) : isManager ? (
        <div className="pl-[50px]">
          <div className="p-[15px] rounded-[5px] flex flex-col gap-2.5 bg-[#f5f5f5]">
            <div className="flex gap-[5px] items-center">
              <CommonProfile
                src={user?.profileImage?.filePath}
                sizeVariant="reply"
              />
              <div className="flex-1 text-sm font-medium">{user.nickname}</div>
            </div>
            <div className="relative">
              <CommonTextArea
                placeholder={
                  "이용자의 평가에 대한 댓글을 남겨주세요\n(10자 이상)"
                }
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                }}
              />
              <CommonButton
                variant="tertiary"
                className="absolute bottom-2.5 right-2.5 px-4 py-2"
                onClick={async () => {
                  if (comment.length < 10) {
                    open("댓글은 10자 이상 입력해주세요.");
                    return;
                  }

                  setIsLoading(true);
                  try {
                    if (type === "incommodity") {
                      await postIncommoditySatisfactionReply(serviceId, {
                        comment,
                      });
                    } else {
                      await postEtcSatisfactionReply(serviceId, {
                        serviceName: type,
                        comment,
                      });
                    }
                    setComment("");
                    await onLoad?.();
                  } catch (e: any) {
                    if (e?.response?.data?.message) {
                      open(e.response.data.message);
                    } else {
                      open("댓글 등록에 실패했습니다.");
                    }
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                등록
              </CommonButton>
            </div>
          </div>
        </div>
      ) : null}
      {simplePopupComponent}
    </>
  );
};
